import { Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import {
    ESGScopeData,
    UpsertESGRequest,
    ESGScopeDataType,
} from "../../../WebApiClient";

@Component({
    selector: "app-sustainability-esg-scope",
    templateUrl: "./sustainability-esg-scope.component.html",
    styleUrls: ["./sustainability-esg-scope.component.scss"],
    standalone: false
})
export class SustainabilityEsgScopeComponent implements OnInit {
    @Input() scopeNumber: number = 0;
    @Input() esgSelected: UpsertESGRequest | undefined = undefined;

    @Output()
    formIsValid = new EventEmitter<boolean>();

    @Output()
    scopeData = new EventEmitter<UpsertESGRequest>();

    @Output()
    formIsDirty = new EventEmitter<boolean>();

    scopeForm: FormGroup = new FormGroup({
        energyProcesses: new FormControl(0, [
            Validators.required,
            Validators.pattern("^[0-9]*$"),
        ]),
        procurement: new FormControl(0, [
            Validators.required,
            Validators.pattern("^[0-9]*$"),
        ]),
        transport: new FormControl(0, [
            Validators.required,
            Validators.pattern("^[0-9]*$"),
        ]),
        wasteRecycling: new FormControl(0, [
            Validators.required,
            Validators.pattern("^[0-9]*$"),
        ]),
        soldProducts: new FormControl(0, [
            Validators.required,
            Validators.pattern("^[0-9]*$"),
        ]),
        total: new FormControl(
            { value: 0, disabled: true },
            Validators.required
        ),
    });

    scopeIndexSelected: number = -1;

    constructor() {
        this.scopeForm.valueChanges.subscribe((value) => {
            if(this.scopeForm.dirty && this.scopeForm.touched) {
                this.formIsDirty.emit(true);
            }
        });
    }

    private calculateTotal(): void {
        this.totalControl.setValue(
            this.tryParseFloat(this.energyProcessesControl.value) +
                this.tryParseFloat(this.procurementControl.value) +
                this.tryParseFloat(this.transportControl.value) +
                this.tryParseFloat(this.wasteRecyclingControl.value) +
                this.tryParseFloat(this.soldProductsControl.value)
        );

        this.setScopeDataValue(
            this.tryParseFloat(this.energyProcessesControl.value),
            ESGScopeDataType.EnergyProcesses
        );
        this.setScopeDataValue(
            this.tryParseFloat(this.procurementControl.value),
            ESGScopeDataType.Procurement
        );
        this.setScopeDataValue(
            this.tryParseFloat(this.transportControl.value),
            ESGScopeDataType.Transport
        );
        this.setScopeDataValue(
            this.tryParseFloat(this.wasteRecyclingControl.value),
            ESGScopeDataType.WasteRecycling
        );
        this.setScopeDataValue(
            this.tryParseFloat(this.soldProductsControl.value),
            ESGScopeDataType.SoldProducts
        );

        setTimeout(() => {
            this.formIsValid.emit(this.scopeForm.valid);
            this.scopeData.emit(this.esgSelected);
        }, 1);
    }

    private setScopeDataValue(
        value: number,
        esgScopeDataType: ESGScopeDataType
    ): void {
        if (
            this.esgSelected &&
            this.esgSelected.scopeList &&
            this.scopeIndexSelected != -1 &&
            this.esgSelected.scopeList[this.scopeIndexSelected].scopeDataList
        ) {
            let scopeData = this.esgSelected.scopeList[
                this.scopeIndexSelected
            ].scopeDataList?.find(
                (c) => c.esgScopeDataType == esgScopeDataType
            );
            if (scopeData) {
                scopeData.value = value;
            }
        }
    }

    private tryParseFloat(value: string): number {
        let result = parseFloat(value);
        if (isNaN(result)) {
            return 0;
        }
        return result;
    }

    ngOnInit(): void {
        if (this.esgSelected && this.esgSelected.scopeList) {
            this.scopeIndexSelected = this.esgSelected.scopeList.findIndex(
                (s) => s.number == this.scopeNumber
            );
            if (this.scopeIndexSelected === -1) {
                return;
            }

            let scopeDataList: ESGScopeData[] =
                this.esgSelected.scopeList[this.scopeIndexSelected]
                    .scopeDataList!;

            let energyProcesses = scopeDataList.find(
                (s) => s.esgScopeDataType === ESGScopeDataType.EnergyProcesses
            );
            let procurement = scopeDataList.find(
                (s) => s.esgScopeDataType === ESGScopeDataType.Procurement
            );
            let transport = scopeDataList.find(
                (s) => s.esgScopeDataType === ESGScopeDataType.Transport
            );
            let wasteRecycling = scopeDataList.find(
                (s) => s.esgScopeDataType === ESGScopeDataType.WasteRecycling
            );
            let soldProducts = scopeDataList.find(
                (s) => s.esgScopeDataType === ESGScopeDataType.SoldProducts
            );

            this.scopeForm.setValue({
                energyProcesses: energyProcesses ? energyProcesses.value : 0,
                procurement: procurement ? procurement.value : 0,
                transport: transport ? transport.value : 0,
                wasteRecycling: wasteRecycling ? wasteRecycling.value : 0,
                soldProducts: soldProducts ? soldProducts.value : 0,
                total: 0,
            });
        }

        this.energyProcessesControl.valueChanges.subscribe((value) => {
            this.calculateTotal();
        });

        this.procurementControl.valueChanges.subscribe((value) => {
            this.calculateTotal();
        });

        this.transportControl.valueChanges.subscribe((value) => {
            this.calculateTotal();
        });

        this.wasteRecyclingControl.valueChanges.subscribe((value) => {
            this.calculateTotal();
        });

        this.soldProductsControl.valueChanges.subscribe((value) => {
            this.calculateTotal();
        });

        this.calculateTotal();
    }

    get energyProcessesControl(): FormControl {
        return this.scopeForm.get("energyProcesses") as FormControl;
    }

    get procurementControl(): FormControl {
        return this.scopeForm.get("procurement") as FormControl;
    }
    get transportControl(): FormControl {
        return this.scopeForm.get("transport") as FormControl;
    }
    get wasteRecyclingControl(): FormControl {
        return this.scopeForm.get("wasteRecycling") as FormControl;
    }
    get soldProductsControl(): FormControl {
        return this.scopeForm.get("soldProducts") as FormControl;
    }
    get totalControl(): FormControl {
        return this.scopeForm.get("total") as FormControl;
    }
}
